/*
  Service & AI product content dictionary.
  Phase B+C scaffold — short, outcome-driven copy in EN + ES. Designed to scale
  to 50–100 landing pages without changing the navigation again.

  Schema per slug:
    {
      category, ai (bool), comingSoon (bool),
      en: { title, tagline, intro, pillars: [{eyebrow,body}], benefits: [string], tech: [string] },
      es: { … same shape … },
      related: [{ label, href }]   // internal cross-links
    }

  Keep copy SHORT. Phase B/C goal is architecture + SEO foundation, not long-form.
*/

const c = (en, es) => ({ en, es });

export const SERVICE_CONTENT = {
  /* ---------------- Restaurant Technology ---------------- */
  'pos-management': {
    category: c('Restaurant Technology', 'Tecnología para Restaurantes'),
    en: {
      title: 'POS Management',
      tagline: 'Your menu, modifiers and platforms — managed for you.',
      intro: 'Stop wrestling with menus across POS, ordering and delivery apps. We manage everything in one place so your team can focus on guests.',
      pillars: [
        { eyebrow: 'Centralize', body: 'One source of truth across POS and ordering channels.' },
        { eyebrow: 'Automate', body: 'Sync menus, modifiers and 86s instantly everywhere.' },
        { eyebrow: 'Grow', body: 'Open new platforms in days — not weeks.' },
      ],
      benefits: ['Lower error rates', 'Faster menu changes', 'Consistent guest experience'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed'],
    },
    es: {
      title: 'Gestión de POS',
      tagline: 'Tu menú, modificadores y plataformas — gestionados por nosotros.',
      intro: 'Deja de pelear con menús entre POS, pedidos online y apps de delivery. Lo gestionamos todo en un solo lugar.',
      pillars: [
        { eyebrow: 'Centraliza', body: 'Una sola fuente de verdad para POS y canales.' },
        { eyebrow: 'Automatiza', body: 'Sincroniza menús y modificadores al instante.' },
        { eyebrow: 'Crece', body: 'Activa nuevas plataformas en días, no semanas.' },
      ],
      benefits: ['Menos errores', 'Cambios de menú más rápidos', 'Experiencia consistente'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed'],
    },
    related: [
      { label: 'IT & POS Systems', href: '/it-pos-systems' },
      { label: 'POS Integrations', href: '/services/pos-integrations' },
      { label: 'Online Ordering', href: '/services/online-ordering' },
      { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
    ],
  },
  'pos-integrations': {
    category: c('Restaurant Technology', 'Tecnología para Restaurantes'),
    en: {
      title: 'POS Integrations',
      tagline: 'Connect your POS to the full restaurant ecosystem.',
      intro: 'Unlock the value of your POS data by wiring it into ordering, delivery, marketing and analytics.',
      pillars: [
        { eyebrow: 'Connect', body: 'POS, online ordering, delivery, marketing and analytics in sync.' },
        { eyebrow: 'Unify', body: 'One source of truth for revenue and operations.' },
        { eyebrow: 'Scale', body: 'New tools plug in cleanly without rewiring.' },
      ],
      benefits: ['Cleaner reporting', 'Real-time inventory', 'Faster ops decisions'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed', 'DoorDash', 'Uber Eats'],
    },
    es: {
      title: 'Integraciones de POS',
      tagline: 'Conecta tu POS con todo el ecosistema del restaurante.',
      intro: 'Aprovecha los datos de tu POS al conectarlo con pedidos, delivery, marketing y analítica.',
      pillars: [
        { eyebrow: 'Conecta', body: 'POS, pedidos, delivery, marketing y analítica en sincronía.' },
        { eyebrow: 'Unifica', body: 'Una fuente de verdad para ingresos y operaciones.' },
        { eyebrow: 'Escala', body: 'Nuevas herramientas se conectan sin rehacer todo.' },
      ],
      benefits: ['Reportes más limpios', 'Inventario en tiempo real', 'Decisiones más rápidas'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed', 'DoorDash', 'Uber Eats'],
    },
    related: [
      { label: 'POS Management', href: '/services/pos-management' },
      { label: 'Data & Analytics', href: '/services/data-analytics' },
      { label: 'AI Agents', href: '/ai-agents-for-restaurants' },
    ],
  },
  'online-ordering': {
    category: c('Restaurant Technology', 'Tecnología para Restaurantes'),
    en: {
      title: 'Online Ordering',
      tagline: 'Direct orders that beat the delivery apps.',
      intro: 'A direct ordering experience tuned to your menu, your brand and your margins — fully integrated with your POS.',
      pillars: [
        { eyebrow: 'Direct', body: 'Cut commissions, own the guest relationship.' },
        { eyebrow: 'Integrated', body: 'Orders flow straight into your POS — no double entry.' },
        { eyebrow: 'Optimized', body: 'Conversion-focused design and mobile-first checkout.' },
      ],
      benefits: ['Higher direct order share', 'Lower commissions', 'First-party customer data'],
      tech: ['Toast Online Ordering', 'Square Online', 'Custom integrations'],
    },
    es: {
      title: 'Pedidos Online',
      tagline: 'Pedidos directos que superan a las apps de delivery.',
      intro: 'Una experiencia de pedidos directos alineada a tu menú, marca y márgenes — integrada con tu POS.',
      pillars: [
        { eyebrow: 'Directo', body: 'Reduce comisiones y dueña la relación con el cliente.' },
        { eyebrow: 'Integrado', body: 'Pedidos fluyen directo al POS — sin doble entrada.' },
        { eyebrow: 'Optimizado', body: 'Diseño enfocado en conversión y checkout móvil.' },
      ],
      benefits: ['Más pedidos directos', 'Menos comisiones', 'Datos propios del cliente'],
      tech: ['Toast Online Ordering', 'Square Online', 'Integraciones personalizadas'],
    },
    related: [
      { label: 'IT & POS Systems', href: '/it-pos-systems' },
      { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
      { label: 'Customer Marketing', href: '/services/customer-marketing' },
    ],
  },

  /* ---------------- Digital Presence ---------------- */
  'restaurant-websites': {
    category: c('Digital Presence', 'Presencia Digital'),
    en: {
      title: 'Restaurant Websites',
      tagline: 'High-converting restaurant websites that turn visitors into guests.',
      intro: 'Fast, mobile-first websites built around how guests actually search, decide and order.',
      pillars: [
        { eyebrow: 'Fast', body: 'Sub-second loads on every device.' },
        { eyebrow: 'Findable', body: 'SEO-ready architecture out of the box.' },
        { eyebrow: 'Convert', body: 'Built to drive reservations and direct orders.' },
      ],
      benefits: ['More online conversions', 'Better Local SEO', 'Always-updated menus'],
      tech: ['Custom CMS', 'Webflow', 'Next.js', 'WordPress'],
    },
    es: {
      title: 'Sitios Web para Restaurantes',
      tagline: 'Sitios web que convierten visitantes en comensales.',
      intro: 'Sitios rápidos y mobile-first construidos sobre cómo los clientes buscan, deciden y piden.',
      pillars: [
        { eyebrow: 'Rápidos', body: 'Carga inferior a 1s en cualquier dispositivo.' },
        { eyebrow: 'Encontrables', body: 'Arquitectura SEO lista desde el primer día.' },
        { eyebrow: 'Convierten', body: 'Diseñados para impulsar reservas y pedidos directos.' },
      ],
      benefits: ['Más conversiones online', 'Mejor SEO local', 'Menús siempre actualizados'],
      tech: ['CMS personalizado', 'Webflow', 'Next.js', 'WordPress'],
    },
    related: [
      { label: 'Local SEO', href: '/services/local-seo' },
      { label: 'Google Business Profile', href: '/services/google-business-profile' },
      { label: 'Customer Marketing', href: '/services/customer-marketing' },
      { label: 'AI Agents', href: '/ai-agents-for-restaurants' },
    ],
  },
  'local-seo': {
    category: c('Digital Presence', 'Presencia Digital'),
    en: {
      title: 'Local SEO',
      tagline: 'Rank where local guests are searching.',
      intro: 'A Local SEO program built for restaurants — Maps, neighborhood searches and high-intent dining queries.',
      pillars: [
        { eyebrow: 'Discover', body: 'Be the first result for "near me" searches.' },
        { eyebrow: 'Convert', body: 'Map listings, photos and menus tuned for action.' },
        { eyebrow: 'Track', body: 'Clear weekly reporting on rankings and traffic.' },
      ],
      benefits: ['Higher Maps rankings', 'More dial / direction clicks', 'Stronger neighborhood share'],
      tech: ['Google Business Profile', 'Apple Maps', 'Bing Places'],
    },
    es: {
      title: 'SEO Local',
      tagline: 'Posiciona donde los clientes locales están buscando.',
      intro: 'Un programa de SEO Local pensado para restaurantes — Maps, búsquedas de barrio y consultas de alta intención.',
      pillars: [
        { eyebrow: 'Descubre', body: 'Sé el primer resultado en búsquedas "cerca de mí".' },
        { eyebrow: 'Convierte', body: 'Mapas, fotos y menús optimizados para acción.' },
        { eyebrow: 'Mide', body: 'Reportes semanales claros de rankings y tráfico.' },
      ],
      benefits: ['Mejor posición en Maps', 'Más llamadas y direcciones', 'Más participación de barrio'],
      tech: ['Google Business Profile', 'Apple Maps', 'Bing Places'],
    },
    related: [
      { label: 'Google Business Profile', href: '/services/google-business-profile' },
      { label: 'Index AI', href: '/index-ai' },
      { label: 'Online Reputation', href: '/services/online-reputation' },
    ],
  },
  'google-business-profile': {
    category: c('Digital Presence', 'Presencia Digital'),
    en: {
      title: 'Google Business Profile',
      tagline: 'Win the Map Pack, the AI Overview citation and the searches that actually put guests in your seats.',
      intro:
        'Google Business Profile is the single highest-leverage free surface a restaurant owns — and the one most operators treat as a one-time setup. In 2026 that stance is no longer survivable. Your profile is the structured input Google, ChatGPT, Perplexity and Apple Intelligence read to compose answers about your venue. We run it as a live operating layer, touched weekly, calibrated monthly, benchmarked against every relevant competitor in your radius.',
      pillars: [
        { eyebrow: 'Rank', body: 'Primary + secondary categories, attributes and structured data that move you into the Map Pack and hold you there.' },
        { eyebrow: 'Respond', body: '95%+ review response coverage across every surface. Median first-response under 4 hours, negative-review recovery under 60 minutes.' },
        { eyebrow: 'Publish', body: 'Weekly Google Posts, monthly photo refresh, on-cadence Q&A moderation. The freshness signals the AI layer reads to trust you.' },
        { eyebrow: 'Convert', body: 'Menu link, order link, reservation link — wired to your direct surfaces, not to the marketplace that keeps 30% of your ticket.' },
        { eyebrow: 'Report', body: 'Impressions, direction clicks, calls, order clicks. Every number benchmarked weekly against your named local competitors.' },
        { eyebrow: 'Recover', body: 'Suspended profiles reinstated. Duplicate listings merged. Category disputes resolved with Google directly.' },
      ],
      benefits: [
        'Higher Map Pack rank in target queries',
        'More direction clicks and phone calls',
        'AI Overview citations for restaurant queries',
        'Faster review response time (industry-leading)',
        'Cleaner, more consistent brand signal across surfaces',
        'Suspension recovery and duplicate merger handled by us',
        'Multi-location parity — every profile treated to the same standard',
        'Weekly operating cadence you never have to run yourself',
      ],
      tech: ['Google Business Profile API', 'Index AI', 'Reputation AI', 'Google Local Services'],
    },
    es: {
      title: 'Perfil de Google Business',
      tagline: 'Gana el Map Pack, la citación en AI Overview y las búsquedas que realmente llenan tus mesas.',
      intro:
        'El Perfil de Google Business es la superficie gratuita de mayor palanca que posee un restaurante — y la que la mayoría trata como una configuración de una sola vez. En 2026 esa postura ya no es sostenible. Tu perfil es la entrada estructurada que Google, ChatGPT, Perplexity y Apple Intelligence leen para componer respuestas sobre tu local. Lo operamos como una capa viva, tocada semanalmente, calibrada mensualmente, comparada contra cada competidor relevante en tu radio.',
      pillars: [
        { eyebrow: 'Rank', body: 'Categorías primaria + secundarias, atributos y datos estructurados que te suben al Map Pack y te mantienen ahí.' },
        { eyebrow: 'Responde', body: '95%+ de cobertura en reseñas. Primera respuesta bajo 4h; recuperación de reseñas negativas en menos de 60 min.' },
        { eyebrow: 'Publica', body: 'Google Posts semanales, refresh de fotos mensual, moderación de Q&A al ritmo correcto. Señales de frescura que la capa IA lee para confiar en ti.' },
        { eyebrow: 'Convierte', body: 'Menú, pedido, reserva — cableados a tus superficies directas, no al marketplace que se queda con el 30% del ticket.' },
        { eyebrow: 'Reporta', body: 'Impresiones, clics de direcciones, llamadas, clics de pedidos. Cada número comparado semanalmente contra tus competidores locales nombrados.' },
        { eyebrow: 'Recupera', body: 'Perfiles suspendidos restablecidos. Duplicados fusionados. Disputas de categoría resueltas directamente con Google.' },
      ],
      benefits: [
        'Mejor posición en el Map Pack para tus queries objetivo',
        'Más clics de direcciones y llamadas',
        'Citaciones en AI Overview para búsquedas de restaurantes',
        'Respuesta más rápida a reseñas (líder en la industria)',
        'Señal de marca más coherente en cada superficie',
        'Recuperación de suspensiones y fusión de duplicados incluida',
        'Paridad multi-local — cada perfil con el mismo estándar',
        'Cadencia operativa semanal que nunca tienes que ejecutar tú',
      ],
      tech: ['API de Google Business Profile', 'Index AI', 'Reputation AI', 'Google Local Services'],
    },
    related: [
      { label: 'Local SEO', href: '/services/local-seo' },
      { label: 'Index AI', href: '/index-ai' },
      { label: 'Online Reputation', href: '/services/online-reputation' },
    ],
  },
  'online-reputation': {
    category: c('Digital Presence', 'Presencia Digital'),
    en: {
      title: 'Online Reputation',
      tagline: 'Every review answered. Every guest heard.',
      intro: 'Reputation management across Google, Yelp, TripAdvisor and Facebook — plus the AI Review Automation that scales it.',
      pillars: [
        { eyebrow: 'Listen', body: 'Centralized inbox across all major review platforms.' },
        { eyebrow: 'Respond', body: 'On-brand replies in your voice, within minutes.' },
        { eyebrow: 'Improve', body: 'Insights that turn feedback into operational change.' },
      ],
      benefits: ['Higher review scores', 'Faster reply time', 'Insights into recurring issues'],
      tech: ['Google', 'Yelp', 'TripAdvisor', 'Facebook'],
    },
    es: {
      title: 'Reputación Online',
      tagline: 'Cada reseña respondida. Cada cliente escuchado.',
      intro: 'Gestión de reputación en Google, Yelp, TripAdvisor y Facebook — con la Automatización de Reseñas con IA que la escala.',
      pillars: [
        { eyebrow: 'Escucha', body: 'Bandeja centralizada de reseñas en todas las plataformas.' },
        { eyebrow: 'Responde', body: 'Respuestas con tu voz, en minutos.' },
        { eyebrow: 'Mejora', body: 'Insights que convierten feedback en cambios reales.' },
      ],
      benefits: ['Mejor puntaje', 'Respuestas más rápidas', 'Insights operativos'],
      tech: ['Google', 'Yelp', 'TripAdvisor', 'Facebook'],
    },
    related: [
      { label: 'Review Automation', href: '/ai/review-automation' },
      { label: 'Google Business Profile', href: '/services/google-business-profile' },
      { label: 'Customer Marketing', href: '/services/customer-marketing' },
    ],
  },

  /* ---------------- Restaurant Growth ---------------- */
  'delivery-optimization': {
    category: c('Restaurant Growth', 'Crecimiento del Restaurante'),
    en: {
      title: 'Delivery Optimization',
      tagline: 'Win the delivery platforms — Uber Eats, DoorDash, Grubhub.',
      intro: 'Menu, promotion and ranking optimization across third-party delivery apps that grows revenue without growing your workload.',
      pillars: [
        { eyebrow: 'Visibility', body: 'Higher ranking inside the apps where guests scroll.' },
        { eyebrow: 'Conversion', body: 'Photos, prices and menu structure that sell.' },
        { eyebrow: 'Promotion', body: 'Smart promo strategy that protects margin.' },
      ],
      benefits: ['Higher delivery revenue', 'Improved ranking', 'Stronger margin protection'],
      tech: ['Uber Eats', 'DoorDash', 'Grubhub'],
    },
    es: {
      title: 'Optimización de Delivery',
      tagline: 'Gana en las apps — Uber Eats, DoorDash, Grubhub.',
      intro: 'Optimización de menú, promoción y posicionamiento en apps de delivery para crecer ingresos sin más carga operativa.',
      pillars: [
        { eyebrow: 'Visibilidad', body: 'Mejor posición dentro de las apps.' },
        { eyebrow: 'Conversión', body: 'Fotos, precios y menú que venden.' },
        { eyebrow: 'Promoción', body: 'Estrategia inteligente que cuida tu margen.' },
      ],
      benefits: ['Más ingresos de delivery', 'Mejor posicionamiento', 'Mejor margen'],
      tech: ['Uber Eats', 'DoorDash', 'Grubhub'],
    },
    related: [
      { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
      { label: 'Online Ordering', href: '/services/online-ordering' },
      { label: 'Data & Analytics', href: '/services/data-analytics' },
    ],
  },
  'catering-growth': {
    category: c('Restaurant Growth', 'Crecimiento del Restaurante'),
    en: {
      title: 'Catering Growth',
      tagline: 'Win corporate catering and large orders, predictably.',
      intro: 'A catering growth program that turns large orders into your most profitable channel.',
      pillars: [
        { eyebrow: 'Find', body: 'Identify corporate prospects and recurring buyers.' },
        { eyebrow: 'Convert', body: 'A catering experience built to close large orders.' },
        { eyebrow: 'Repeat', body: 'Lifecycle marketing that turns one order into many.' },
      ],
      benefits: ['Higher average ticket', 'Repeat corporate buyers', 'Predictable revenue'],
      tech: ['EZ Cater', 'Custom integrations'],
    },
    es: {
      title: 'Crecimiento de Catering',
      tagline: 'Catering corporativo y pedidos grandes, de forma predecible.',
      intro: 'Un programa de catering que convierte pedidos grandes en tu canal más rentable.',
      pillars: [
        { eyebrow: 'Encuentra', body: 'Identifica prospectos corporativos y recurrentes.' },
        { eyebrow: 'Convierte', body: 'Experiencia diseñada para cerrar pedidos grandes.' },
        { eyebrow: 'Repite', body: 'Marketing de ciclo que multiplica los pedidos.' },
      ],
      benefits: ['Mayor ticket promedio', 'Clientes corporativos recurrentes', 'Ingresos predecibles'],
      tech: ['EZ Cater', 'Integraciones personalizadas'],
    },
    related: [
      { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
      { label: 'Catering AI', href: '/ai/catering-ai' },
      { label: 'Customer Marketing', href: '/services/customer-marketing' },
    ],
  },
  'customer-marketing': {
    category: c('Restaurant Growth', 'Crecimiento del Restaurante'),
    en: {
      title: 'Customer Marketing',
      tagline: 'Turn guests into loyal, repeat customers.',
      intro: 'Email, SMS and loyalty marketing built on your POS data — converting first visits into a habit.',
      pillars: [
        { eyebrow: 'Segment', body: 'Smart guest segments built from POS and ordering data.' },
        { eyebrow: 'Engage', body: 'Email, SMS and offers timed to the right moment.' },
        { eyebrow: 'Retain', body: 'Loyalty programs that actually drive a second visit.' },
      ],
      benefits: ['Higher repeat rate', 'Higher lifetime value', 'Direct revenue lift'],
      tech: ['Toast Loyalty', 'Klaviyo', 'Twilio'],
    },
    es: {
      title: 'Marketing al Cliente',
      tagline: 'Convierte clientes en habituales y fieles.',
      intro: 'Email, SMS y lealtad construidos sobre tus datos de POS — convirtiendo primeras visitas en costumbre.',
      pillars: [
        { eyebrow: 'Segmenta', body: 'Segmentos inteligentes desde POS y pedidos.' },
        { eyebrow: 'Activa', body: 'Email, SMS y ofertas en el momento correcto.' },
        { eyebrow: 'Retén', body: 'Lealtad que sí trae la segunda visita.' },
      ],
      benefits: ['Más recurrencia', 'Mayor valor de vida', 'Más ingresos directos'],
      tech: ['Toast Loyalty', 'Klaviyo', 'Twilio'],
    },
    related: [
      { label: 'Restaurant Websites', href: '/services/restaurant-websites' },
      { label: 'AI Agents', href: '/ai-agents-for-restaurants' },
      { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
    ],
  },

  /* ---------------- Restaurant Intelligence ---------------- */
  'data-analytics': {
    category: c('Restaurant Intelligence', 'Inteligencia del Restaurante'),
    en: {
      title: 'Data & Analytics',
      tagline: 'Know exactly what is driving (and draining) your restaurant.',
      intro: 'POS, ordering, delivery, marketing and guest data — modeled into the KPIs that actually move the business.',
      pillars: [
        { eyebrow: 'Connect', body: 'All revenue and operational data in one place.' },
        { eyebrow: 'Model', body: 'KPIs that match how you run the restaurant.' },
        { eyebrow: 'Decide', body: 'Clear weekly views with recommended actions.' },
      ],
      benefits: ['Clearer P&L picture', 'Faster decisions', 'Spot leaks before they hurt'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed', 'Google Analytics'],
    },
    es: {
      title: 'Datos y Analítica',
      tagline: 'Sabe exactamente qué impulsa (y qué frena) tu restaurante.',
      intro: 'Datos de POS, pedidos, delivery, marketing y clientes — convertidos en los KPIs que sí mueven el negocio.',
      pillars: [
        { eyebrow: 'Conecta', body: 'Todos los datos en un solo lugar.' },
        { eyebrow: 'Modela', body: 'KPIs que reflejan tu operación.' },
        { eyebrow: 'Decide', body: 'Vistas semanales con acciones recomendadas.' },
      ],
      benefits: ['Mejor visión del P&L', 'Decisiones más rápidas', 'Detección temprana'],
      tech: ['Toast', 'Clover', 'Square', 'Lightspeed', 'Google Analytics'],
    },
    related: [
      { label: 'Dashboards', href: '/services/dashboards' },
      { label: 'Performance Reporting', href: '/services/performance-reporting' },
      { label: 'Business Intelligence', href: '/services/business-intelligence' },
      { label: 'AI Solutions', href: '/index-ai' },
    ],
  },
  dashboards: {
    category: c('Restaurant Intelligence', 'Inteligencia del Restaurante'),
    en: {
      title: 'Dashboards',
      tagline: 'Real-time visibility you actually open.',
      intro: 'Restaurant-grade dashboards designed for owners and operators, not data analysts.',
      pillars: [
        { eyebrow: 'Live', body: 'Real-time revenue, orders and operations.' },
        { eyebrow: 'Focused', body: 'Five numbers that matter — not fifty that do not.' },
        { eyebrow: 'Mobile', body: 'Built for the phone first.' },
      ],
      benefits: ['Faster shift decisions', 'Clarity for the team', 'Confidence in numbers'],
      tech: ['Custom', 'Looker Studio', 'Metabase'],
    },
    es: {
      title: 'Dashboards',
      tagline: 'Visibilidad en tiempo real que sí abres.',
      intro: 'Dashboards diseñados para dueños y operadores, no para analistas.',
      pillars: [
        { eyebrow: 'En vivo', body: 'Ingresos, pedidos y operación en tiempo real.' },
        { eyebrow: 'Enfocados', body: 'Cinco números que importan, no cincuenta que no.' },
        { eyebrow: 'Móvil', body: 'Diseñados primero para el celular.' },
      ],
      benefits: ['Mejores decisiones por turno', 'Claridad para el equipo', 'Confianza en datos'],
      tech: ['Personalizado', 'Looker Studio', 'Metabase'],
    },
    related: [
      { label: 'Data & Analytics', href: '/services/data-analytics' },
      { label: 'Performance Reporting', href: '/services/performance-reporting' },
    ],
  },
  'dashboards': {
    category: c('Restaurant Intelligence', 'Inteligencia del Restaurante'),
    en: {
      title: 'Live Dashboards',
      tagline: 'Your whole restaurant on one screen — updated while you sleep.',
      intro: 'Sales in the POS, orders in three delivery apps, reviews on Google, reservations somewhere else. Live Dashboards pull every number into one clear, always-current screen — on your phone, tablet or office TV. No exports, no spreadsheets, no "let me check five apps".',
      pillars: [
        { eyebrow: 'Unify', body: 'POS, delivery platforms, Google, reservations and payments connected once by KITXENS — then synced automatically every day.' },
        { eyebrow: 'Visualize', body: 'Revenue, orders, channel mix, top dishes and review score in a layout designed for restaurant owners, not analysts.' },
        { eyebrow: 'Alert', body: 'When something moves — a sales dip, a rating drop, a delivery spike — you know the same day, not at month end.' },
      ],
      benefits: ['One trustworthy number for every question', 'Check your restaurant from anywhere in 30 seconds', 'Spot problems the day they start, not weeks later', 'Zero manual work — KITXENS maintains every connection'],
      tech: ['POS integrations', 'Delivery platforms', 'Google Business', 'Payments'],
    },
    es: {
      title: 'Dashboards en Vivo',
      tagline: 'Todo tu restaurante en una pantalla — actualizado mientras duermes.',
      intro: 'Las ventas en el POS, los pedidos en tres apps de delivery, las reseñas en Google, las reservas en otro lado. Los Dashboards en Vivo unifican cada número en una sola pantalla clara y siempre al día — en tu móvil, tablet o TV de oficina. Sin exportar, sin hojas de cálculo, sin "déjame revisar cinco apps".',
      pillars: [
        { eyebrow: 'Unifica', body: 'POS, plataformas de delivery, Google, reservas y pagos conectados una vez por KITXENS — y sincronizados automáticamente cada día.' },
        { eyebrow: 'Visualiza', body: 'Ingresos, pedidos, mix de canales, platos top y puntaje de reseñas en un diseño para dueños de restaurante, no para analistas.' },
        { eyebrow: 'Alerta', body: 'Cuando algo se mueve — caída de ventas, baja de rating, pico de delivery — lo sabes el mismo día, no a fin de mes.' },
      ],
      benefits: ['Un solo número confiable para cada pregunta', 'Revisa tu restaurante desde donde sea en 30 segundos', 'Detecta problemas el día que empiezan, no semanas después', 'Cero trabajo manual — KITXENS mantiene cada conexión'],
      tech: ['Integraciones POS', 'Plataformas de delivery', 'Google Business', 'Pagos'],
    },
    related: [
      { label: 'Data & Analytics', href: '/services/data-analytics' },
      { label: 'Performance Reporting', href: '/services/performance-reporting' },
      { label: 'Business Intelligence', href: '/services/business-intelligence' },
    ],
  },
  'performance-reporting': {
    category: c('Restaurant Intelligence', 'Inteligencia del Restaurante'),
    en: {
      title: 'Performance Reporting',
      tagline: 'Executive reporting that ends in a recommended action.',
      intro: 'Weekly and monthly performance reports with the why behind every number — and what to do next.',
      pillars: [
        { eyebrow: 'Clarity', body: 'Plain-language summaries, no jargon.' },
        { eyebrow: 'Insight', body: 'The story behind the numbers.' },
        { eyebrow: 'Action', body: 'A recommended next step on every report.' },
      ],
      benefits: ['Sharper weekly decisions', 'Aligned leadership team', 'Compounding improvements'],
      tech: ['Custom reporting', 'Google Sheets', 'Looker Studio'],
    },
    es: {
      title: 'Reportes de Rendimiento',
      tagline: 'Reportes ejecutivos que terminan en una acción recomendada.',
      intro: 'Reportes semanales y mensuales con el porqué de cada número — y qué hacer a continuación.',
      pillars: [
        { eyebrow: 'Claridad', body: 'Resúmenes en lenguaje claro.' },
        { eyebrow: 'Insight', body: 'La historia detrás del número.' },
        { eyebrow: 'Acción', body: 'Una recomendación concreta en cada reporte.' },
      ],
      benefits: ['Mejores decisiones semanales', 'Liderazgo alineado', 'Mejora continua'],
      tech: ['Reportes personalizados', 'Google Sheets', 'Looker Studio'],
    },
    related: [
      { label: 'Data & Analytics', href: '/services/data-analytics' },
      { label: 'Dashboards', href: '/services/dashboards' },
    ],
  },
  'business-intelligence': {
    category: c('Restaurant Intelligence', 'Inteligencia del Restaurante'),
    en: {
      title: 'Business Intelligence',
      tagline: 'Data-driven decisions, every shift.',
      intro: 'A restaurant-focused BI program — the right metrics, the right cadence, in the hands of the people who act on them.',
      pillars: [
        { eyebrow: 'Define', body: 'The metrics that fit your operation.' },
        { eyebrow: 'Deliver', body: 'In the moment the decision is being made.' },
        { eyebrow: 'Decide', body: 'Confident calls, not gut calls.' },
      ],
      benefits: ['Higher operational efficiency', 'Lower waste', 'Better team alignment'],
      tech: ['Custom BI stack'],
    },
    es: {
      title: 'Business Intelligence',
      tagline: 'Decisiones basadas en datos, cada turno.',
      intro: 'Un programa de BI enfocado en restaurantes — las métricas correctas, en el momento correcto, con quien decide.',
      pillars: [
        { eyebrow: 'Define', body: 'Las métricas que se ajustan a tu operación.' },
        { eyebrow: 'Entrega', body: 'En el momento de decidir.' },
        { eyebrow: 'Decide', body: 'Con confianza, no por intuición.' },
      ],
      benefits: ['Más eficiencia operativa', 'Menos desperdicio', 'Mejor alineación'],
      tech: ['BI personalizado'],
    },
    related: [
      { label: 'Data & Analytics', href: '/services/data-analytics' },
      { label: 'Performance Reporting', href: '/services/performance-reporting' },
      { label: 'AI Solutions', href: '/index-ai' },
    ],
  },

  /* ---------------- AI Products ---------------- */
  'custom-ai-automation': {
    category: c('AI Solutions', 'Soluciones IA'),
    ai: true,
    en: {
      title: 'Custom AI Automation',
      tagline: 'The work your team dreads, done automatically — every single day.',
      intro: 'Every restaurant loses hours to the same invisible tasks: retyping orders, chasing confirmations, updating menus across five platforms, copying numbers into spreadsheets. We design AI workflows around how your restaurant actually operates — and then we run them for you. You never touch the technology; you just watch the busywork disappear.',
      pillars: [
        { eyebrow: 'Map', body: 'We shadow your operation and identify the workflows silently eating your team\'s time — most owners are shocked by the total.' },
        { eyebrow: 'Build', body: 'KITXENS designs and deploys AI that bridges your existing tools — POS, delivery apps, Google, WhatsApp, email — with zero disruption to service.' },
        { eyebrow: 'Improve', body: 'Automations aren\'t installed, they\'re managed. We monitor, tune and expand them every month as your operation evolves.' },
      ],
      benefits: ['15–20 hours back every week', 'Fewer manual errors and missed follow-ups', 'One system instead of five disconnected tools', 'Efficiency that compounds month after month'],
      tech: ['KITXENS AI Operations Layer', 'Secure system integrations', 'Continuous monitoring & tuning'],
    },
    es: {
      title: 'Automatización IA Personalizada',
      tagline: 'El trabajo que tu equipo odia, hecho automáticamente — todos los días.',
      intro: 'Todo restaurante pierde horas en las mismas tareas invisibles: reescribir pedidos, perseguir confirmaciones, actualizar menús en cinco plataformas, copiar números a hojas de cálculo. Diseñamos flujos de IA alrededor de cómo opera tu restaurante en realidad — y los gestionamos por ti. Tú nunca tocas la tecnología; solo ves desaparecer el trabajo repetitivo.',
      pillars: [
        { eyebrow: 'Mapea', body: 'Observamos tu operación e identificamos los flujos que consumen el tiempo de tu equipo en silencio — a la mayoría de dueños les sorprende el total.' },
        { eyebrow: 'Construye', body: 'KITXENS diseña y despliega IA que conecta tus herramientas actuales — POS, apps de delivery, Google, WhatsApp, email — sin interrumpir el servicio.' },
        { eyebrow: 'Mejora', body: 'Las automatizaciones no se instalan, se gestionan. Las monitoreamos, ajustamos y ampliamos cada mes conforme evoluciona tu operación.' },
      ],
      benefits: ['15–20 horas recuperadas cada semana', 'Menos errores manuales y seguimientos perdidos', 'Un solo sistema en lugar de cinco herramientas sueltas', 'Eficiencia que se acumula mes tras mes'],
      tech: ['Capa de Operaciones IA de KITXENS', 'Integraciones seguras de sistemas', 'Monitoreo y ajuste continuos'],
    },
    related: [
      { label: 'AI Workforce™', href: '/ai-agents-for-restaurants' },
      { label: 'Index AI™', href: '/index-ai' },
      { label: 'Catering AI™', href: '/ai/catering-ai' },
    ],
  },
  'ai-receptionist': {
    category: c('AI Solutions', 'Soluciones IA'),
    ai: true,
    en: {
      title: 'AI Receptionist',
      tagline: 'Never miss a call, a reservation or a question.',
      intro: 'An AI receptionist that answers your phones 24/7 — books reservations, answers FAQs and routes urgent calls instantly.',
      pillars: [
        { eyebrow: 'Always on', body: '24/7 phone coverage in your brand voice.' },
        { eyebrow: 'Smart', body: 'Books reservations, takes orders, answers questions.' },
        { eyebrow: 'Connected', body: 'Hands off seamlessly to your team when needed.' },
      ],
      benefits: ['Zero missed calls', 'More reservations', 'Less front-of-house pressure'],
      tech: ['Twilio', 'OpenAI', 'Custom voice models'],
    },
    es: {
      title: 'Recepcionista IA',
      tagline: 'Nunca pierdas una llamada, reserva o pregunta.',
      intro: 'Un recepcionista de IA que contesta tu teléfono 24/7 — reserva, responde FAQs y deriva urgencias al instante.',
      pillars: [
        { eyebrow: 'Siempre activo', body: 'Cobertura telefónica 24/7 con tu voz de marca.' },
        { eyebrow: 'Inteligente', body: 'Reserva, toma pedidos y responde preguntas.' },
        { eyebrow: 'Conectado', body: 'Transfiere al equipo cuando es necesario.' },
      ],
      benefits: ['Cero llamadas perdidas', 'Más reservas', 'Menos presión en sala'],
      tech: ['Twilio', 'OpenAI', 'Modelos de voz personalizados'],
    },
    related: [
      { label: 'AI Agents', href: '/ai-agents-for-restaurants' },
      { label: 'Customer Marketing', href: '/services/customer-marketing' },
    ],
  },
  'review-automation': {
    category: c('AI Solutions', 'Soluciones IA'),
    ai: true,
    en: {
      title: 'Review Automation',
      tagline: 'Every review answered in minutes — good ones convert, bad ones get rescued.',
      intro: 'Most restaurants reply to fewer than half of their reviews, and Google notices. Review Automation is the part of your AI Workforce™ that answers every single review — Google, Yelp, TripAdvisor, Facebook — in your brand voice, within minutes. Five-star reviews become marketing; critical ones get a fast, human-approved recovery response before they cost you customers.',
      pillars: [
        { eyebrow: 'Listen', body: 'Every review from every platform lands in one place the moment it\'s posted — nothing slips through a busy service.' },
        { eyebrow: 'Respond', body: 'AI drafts an on-brand reply in minutes. Positive reviews publish automatically; sensitive ones wait for your one-tap approval.' },
        { eyebrow: 'Improve', body: 'Recurring complaints ("slow delivery", "cold fries") are surfaced monthly so you fix the cause, not just the comment.' },
      ],
      benefits: ['100% of reviews answered — reply time from days to minutes', 'Higher local ranking: Google rewards active response rates', 'Unhappy guests recovered before they become lost customers', 'A monthly voice-of-customer report from your own reviews'],
      tech: ['Google', 'Yelp', 'TripAdvisor', 'Facebook'],
    },
    es: {
      title: 'Automatización de Reseñas',
      tagline: 'Cada reseña respondida en minutos — las buenas convierten, las malas se rescatan.',
      intro: 'La mayoría de restaurantes responde a menos de la mitad de sus reseñas, y Google lo nota. Review Automation es la parte de tu AI Workforce™ que responde absolutamente todas — Google, Yelp, TripAdvisor, Facebook — con tu voz de marca y en minutos. Las de 5 estrellas se convierten en marketing; las críticas reciben una respuesta de recuperación rápida y aprobada por ti antes de costarte clientes.',
      pillars: [
        { eyebrow: 'Escucha', body: 'Cada reseña de cada plataforma llega a un solo lugar en el momento en que se publica — nada se pierde en un servicio ocupado.' },
        { eyebrow: 'Responde', body: 'La IA redacta una respuesta con tu marca en minutos. Las positivas se publican solas; las sensibles esperan tu aprobación con un toque.' },
        { eyebrow: 'Mejora', body: 'Las quejas recurrentes ("delivery lento", "papas frías") se reportan cada mes para que arregles la causa, no solo el comentario.' },
      ],
      benefits: ['100% de reseñas respondidas — de días a minutos', 'Mejor ranking local: Google premia la tasa de respuesta activa', 'Clientes molestos recuperados antes de perderlos', 'Reporte mensual de voz-del-cliente desde tus propias reseñas'],
      tech: ['Google', 'Yelp', 'TripAdvisor', 'Facebook'],
    },
    related: [
      { label: 'AI Workforce™', href: '/ai-agents-for-restaurants' },
      { label: 'Index AI™', href: '/index-ai' },
      { label: 'Online Reputation', href: '/services/online-reputation' },
    ],
  },
  'catering-ai': {
    category: c('AI Solutions', 'Soluciones IA'),
    ai: true,
    comingSoon: true,
    en: {
      title: 'Catering AI',
      tagline: 'The complete AI-powered catering growth platform — being finalized.',
      intro: 'Catering AI is our most ambitious platform: predictable, AI-driven growth for catering and large-order revenue. Currently being finalized and available soon.',
      pillars: [
        { eyebrow: 'Predict', body: 'Find catering buyers before they ask.' },
        { eyebrow: 'Convert', body: 'AI-personalized outreach that closes large orders.' },
        { eyebrow: 'Retain', body: 'Lifecycle automation that turns one order into many.' },
      ],
      benefits: ['Predictable catering pipeline', 'Higher repeat rate', 'Less manual outreach'],
      tech: ['EZ Cater', 'OpenAI', 'Custom orchestration'],
    },
    es: {
      title: 'Catering AI',
      tagline: 'La plataforma completa de crecimiento de catering con IA — en fase final.',
      intro: 'Catering AI es nuestra plataforma más ambiciosa: crecimiento predecible para catering y pedidos grandes con IA. Actualmente en fase final, disponible muy pronto.',
      pillars: [
        { eyebrow: 'Predice', body: 'Encuentra clientes de catering antes de que pregunten.' },
        { eyebrow: 'Convierte', body: 'Outreach personalizado que cierra pedidos grandes.' },
        { eyebrow: 'Retiene', body: 'Automatización que convierte un pedido en muchos.' },
      ],
      benefits: ['Pipeline predecible', 'Mayor recurrencia', 'Menos trabajo manual'],
      tech: ['EZ Cater', 'OpenAI', 'Orquestación personalizada'],
    },
    related: [
      { label: 'Catering Growth', href: '/services/catering-growth' },
      { label: 'AI Agents', href: '/ai-agents-for-restaurants' },
    ],
  },
};

/* Future architecture — stubs that exist in the routing/sitemap but are not in the nav yet. */
export const FUTURE_SLUGS = {
  services: [
    'voice-ai', 'franchise-ai', 'restaurant-ai-platform', 'ai-consulting', 'ai-strategy',
    'restaurant-technology-assessment', 'technology-migration', 'digital-transformation',
    'restaurant-automation-services',
  ],
};

export function resolveContent(slug, prefix = 'services') {
  const entry = SERVICE_CONTENT[slug];
  if (entry) return entry;
  // Synthesize a Coming Soon entry for future slugs
  const isFuture = FUTURE_SLUGS.services.includes(slug);
  if (isFuture || prefix === 'ai') {
    const ACRONYMS = new Set(['ai', 'pos', 'seo', 'bi', 'crm', 'api', 'ux', 'ui', 'it']);
    const human = slug
      .split('-')
      .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
    return {
      category: c(prefix === 'ai' ? 'AI Solutions' : 'Services', prefix === 'ai' ? 'Soluciones IA' : 'Servicios'),
      ai: prefix === 'ai',
      comingSoon: true,
      en: { title: human, tagline: 'Being finalized on the Kitxens platform.', intro: 'We are preparing this page. Reach out if you want to be the first to know.', pillars: [], benefits: [], tech: [] },
      es: { title: human, tagline: 'En fase final en la plataforma Kitxens.', intro: 'Estamos preparando esta página. Contáctanos si quieres ser de los primeros.', pillars: [], benefits: [], tech: [] },
      related: [{ label: 'Contact', href: '/contact' }, { label: 'How It Works', href: '/how-it-works' }],
    };
  }
  return null;
}
